import { apiFetch } from '@/lib/http';

export class AuthenticatedDownloadError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthenticatedDownloadError';
    this.status = status;
  }
}

/**
 * Parse Content-Disposition filename (RFC 6266 / common server variants).
 * Prefer filename*=UTF-8''… then quoted filename= then bare filename=.
 */
export function parseContentDispositionFilename(
  header: string | null | undefined,
  fallback: string,
): string {
  if (!header) return fallback;

  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      return utf8Match[1].trim();
    }
  }

  const quotedMatch = /filename\s*=\s*"((?:\\.|[^"\\])*)"/i.exec(header);
  if (quotedMatch?.[1]) {
    return quotedMatch[1].replace(/\\"/g, '"');
  }

  const bareMatch = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bareMatch?.[1]) {
    return bareMatch[1].trim().replace(/^["']|["']$/g, '');
  }

  return fallback;
}

function messageForDownloadStatus(status: number): string {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again and retry the download.';
    case 403:
    case 404:
      return 'You do not have permission to download this export, or it is not available.';
    default:
      if (status >= 500) {
        return 'Export generation failed. Please try again or contact support.';
      }
      return `Download failed (HTTP ${status}).`;
  }
}

/**
 * Authenticated binary download via apiFetch → blob → object URL.
 * Preserves server Content-Disposition filename; revokes the object URL after click.
 */
export async function downloadAuthenticatedFile(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const res = await apiFetch(path, {
    method: 'GET',
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*',
    },
  });

  if (!res.ok) {
    throw new AuthenticatedDownloadError(res.status, messageForDownloadStatus(res.status));
  }

  const blob = await res.blob();
  const filename = parseContentDispositionFilename(
    res.headers.get('Content-Disposition'),
    fallbackFilename,
  );

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
